import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Buildings,
  FileText,
  GearSix,
  House,
  LinkSimple,
  ListChecks,
  Pulse,
  Receipt,
  SignOut,
  UserCircle,
  Users,
  Wallet,
} from "@phosphor-icons/react";
import {
  Command,
  CommandEmpty,
  CommandFooter,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Kbd,
  KbdGroup,
} from "@hulahoop/ui";
import { ROUTES } from "@/app/routes";
import { useCopyPageLink } from "./CopyPageLink";
import { SHELL_CREATE_ACTIONS, startShellCreate } from "./shellCreate";

const SECTIONS = [
  { label: "nav.dashboard", path: ROUTES.DASHBOARD, icon: House },
  { label: "shell.pending", path: ROUTES.TASKS, icon: ListChecks },
  { label: "shell.activity", path: ROUTES.ACTIVITY, icon: Pulse },
  { label: "nav.beneficiaries", path: ROUTES.BENEFICIARIES.LIST, icon: Buildings },
  { label: "nav.investors", path: ROUTES.INVESTMENTS.INVESTORS, icon: Users },
  { label: "nav.investments", path: ROUTES.INVESTMENTS.MAIN, icon: Wallet },
  { label: "shell.quotas", path: ROUTES.QUOTAS, icon: Receipt },
  { label: "nav.contracts", path: ROUTES.INVESTMENTS.CONTRACTS, icon: FileText },
  { label: "shell.settings", path: ROUTES.PROFILE, icon: GearSix },
];

interface ShellCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSignOut: () => void;
}

export function ShellCommandPalette({ open, onOpenChange, onSignOut }: ShellCommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { copy } = useCopyPageLink();
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (open) setQuery("");
  }, [open]);
  const handleClose = () => onOpenChange(false);
  const go = (path: string) => {
    handleClose();
    navigate(path);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        width="lg"
        placement="top"
        className="gap-0 overflow-hidden p-0"
        showCloseButton={false}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t("shell.search_title")}</DialogTitle>
          <DialogDescription>{t("shell.search_description")}</DialogDescription>
        </DialogHeader>
        <Command>
          <CommandInput
            size="lg"
            placeholder={t("shell.search_placeholder")}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="max-h-96 p-1">
            <CommandEmpty>{t("shell.no_results")}</CommandEmpty>
            <CommandGroup heading={t("shell.actions")}>
              {SHELL_CREATE_ACTIONS.map(({ label, hint, kind, icon: Icon }) => (
                <CommandItem
                  key={kind}
                  value={t(label)}
                  onSelect={() => {
                    handleClose();
                    startShellCreate(kind);
                  }}
                >
                  <Icon /> {t(label)}
                  {hint && <span className="text-muted-foreground">{t(hint)}</span>}
                </CommandItem>
              ))}
              <CommandItem
                value={t("shell.copy_link")}
                onSelect={() => {
                  handleClose();
                  void copy();
                }}
              >
                <LinkSimple /> {t("shell.copy_link")}
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading={t("shell.navigation")}>
              {SECTIONS.map(({ label, path, icon: Icon }) => (
                <CommandItem
                  key={path}
                  value={t("shell.go_to", { section: t(label) })}
                  onSelect={() => go(path)}
                >
                  <Icon /> {t("shell.go_to", { section: t(label) })}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading={t("shell.settings")}>
              <CommandItem value={t("shell.profile")} onSelect={() => go(ROUTES.PROFILE)}>
                <UserCircle /> {t("shell.profile")}
              </CommandItem>
              <CommandItem
                value={t("nav.logout")}
                onSelect={() => {
                  handleClose();
                  onSignOut();
                }}
              >
                <SignOut /> {t("nav.logout")}
              </CommandItem>
            </CommandGroup>
          </CommandList>
          <CommandFooter>
            <span>
              <KbdGroup>
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd>
              </KbdGroup>
              {t("shell.navigate")}
            </span>
            <span>
              <Kbd>esc</Kbd> {t("shell.close")}
            </span>
            <span className="ml-auto">
              <Kbd>↵</Kbd> {t("shell.select")}
            </span>
          </CommandFooter>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
